import { toUndef } from "@/utils";

import type { CreateProductRequestDto } from "../dtos/create-product-request-dto";
import type { ProductCategoryType } from "../types";
import {
  getCategoryIdFromName,
  mapPackageForCreate,
  mapPackageForEdit
} from "./form-payload";

export type VariantFormValues = {
  variantId?: number;
  quantity: number;
  price: number;
  unit: string;
  provisioningId?: string;
};

export type PackageFormValues = {
  packageId?: number;
  name: string;
  isFreeTrial?: boolean;
  specifications: string[];
  variants: VariantFormValues[];
};

export type BaseProductFormValues = {
  isRecurring?: boolean;
  productName: string;
  productCode?: string;
  productType: string;
  productService: string;
  productDescription?: string;
  specification?: string;
  link?: string;
  industryCategory?: string[];
  provideTrial?: boolean;
  tncTrial?: string;
  tncCheckout: string;
  tncRecurring?: string;
  packages: PackageFormValues[];
  pictures: Array<{
    mediaPath: string;
    type: string;
    file?: File;
    id?: number;
    uid?: string;
  }>;
  apiPackage?: {
    host?: string;
    header?: string;
    key?: string;
    apis?: Array<{
      name?: string;
      endpoint?: string;
      method?: string;
      body?: Array<{ key?: string; type?: ApiBodyType }>;
      response?: string;
    }>;
  };
  showOnBuyer?: boolean;
};

export type EditProductFormValues = BaseProductFormValues;

// ============================================================================
// Industry Category Helpers
// ============================================================================

/**
 * Maps industry category slugs to API payload format
 */
export const mapIndustryCategoryToPayload = (
  industryCategory?: string[]
): { slug_name: string }[] | undefined => {
  if (!industryCategory || industryCategory.length === 0) return undefined;
  return industryCategory.map((slug) => ({ slug_name: slug }));
};

// ============================================================================
// Create Product Payload Builder
// ============================================================================

/**
 * Api body type values
 */
export type ApiBodyType = "int" | "string" | "bool" | "varchar";

/**
 * Builds the full payload for creating a new product
 */
export const buildCreateProductPayload = (
  values: BaseProductFormValues,
  productCategory: ProductCategoryType | undefined,
  companyName: string
) => {
  const categoryId = getCategoryIdFromName(productCategory);
  return {
    is_recurring_available: values.isRecurring ?? false,
    product_category_id: categoryId,
    product_industry_category: mapIndustryCategoryToPayload(
      values.industryCategory
    ),
    name: values.productName,
    product_id: "-",
    product_code: values.productCode,
    product_type_id: +values.productType,
    product_service: values.productService,
    version: "-",
    company_name: companyName,
    is_private: false,
    is_provide_free_trial: values.provideTrial,
    url_sandbox: values.link,
    contact_email: "-",
    description: values.productDescription,
    information: values.specification ?? "information",
    tnc_product_trial: values.tncTrial ?? "",
    tnc_product_checkout: values.tncCheckout ?? "",
    product_package: values.packages.map(mapPackageForCreate),
    product_photo: values.pictures.map((p) => ({
      media_path: p.mediaPath,
      type: p.type as "image" | "thumbnail"
    })),
    ...(categoryId === 1 && {
      product_api_package:
        values.apiPackage as CreateProductRequestDto["product_api_package"]
    }),
    is_show_docs: values.showOnBuyer
  } satisfies CreateProductRequestDto;
};

// ============================================================================
// Edit Product Payload Builder
// ============================================================================

/**
 * Builds the full payload for editing an existing product
 */
export const buildEditProductPayload = (
  values: EditProductFormValues,
  productCategory: ProductCategoryType | undefined,
  companyName: string,
  productId: string,
  currentProductStatus?: string,
  isPublish?: boolean
) => {
  const categoryId = getCategoryIdFromName(productCategory);
  return {
    is_recurring_available: values.isRecurring ?? false,
    id: productId,
    product_category_id: categoryId,
    product_industry_category: mapIndustryCategoryToPayload(
      values.industryCategory
    ),
    is_publish: isPublish,
    product_status: currentProductStatus,
    product_code: values.productCode,
    name: values.productName,
    product_id: "-",
    product_type_id: +values.productType,
    product_service: values.productService,
    version: "-",
    company_name: companyName,
    is_private: false,
    is_provide_free_trial: values.provideTrial,
    url_sandbox: values.link,
    contact_email: "-",
    description: values.productDescription,
    information: values.specification ?? "information",
    tnc_product_trial: values.tncTrial,
    tnc_product_checkout: values.tncCheckout,
    tnc_recurring: toUndef(values.tncRecurring),
    product_package: values.packages.map(mapPackageForEdit),
    product_photo: values.pictures.map((p) => ({
      id: p.id,
      media_path: p.mediaPath,
      type: p.type as "image" | "thumbnail"
    })),
    ...(categoryId === 1 && { product_api_package: values.apiPackage }),
    is_show_docs: values.showOnBuyer ?? false
  };
};

// ============================================================================
// Draft Product Payload Builder
// ============================================================================

/**
 * Builds the payload for saving a product draft (partial data allowed)
 */
export const buildDraftProductPayload = (
  values: BaseProductFormValues | EditProductFormValues,
  productCategory: ProductCategoryType | undefined,
  companyName: string,
  draftId?: string | number,
  packagesDirty = true
) => {
  const categoryId = getCategoryIdFromName(productCategory);

  return {
    is_recurring_available: values.isRecurring ?? false,
    ...(draftId && { id: draftId }),
    product_category_id: categoryId,
    product_industry_category: mapIndustryCategoryToPayload(
      values.industryCategory
    ),
    name: toUndef(values.productName),
    product_id: "-",
    product_code: toUndef(values.productCode),
    product_type_id: values.productType ? +values.productType : undefined,
    product_service: values.productService,
    version: "-",
    company_name: companyName,
    is_provide_free_trial:
      typeof values.provideTrial === "boolean"
        ? values.provideTrial
        : undefined,
    url_sandbox: toUndef(values.link),
    contact_email: "-",
    description: toUndef(values.productDescription),
    information: toUndef(values.specification),
    tnc_product_trial: toUndef(values.tncTrial),
    tnc_product_checkout: toUndef(values.tncCheckout),
    tnc_recurring: toUndef(values.tncRecurring),
    product_photo: values.pictures?.length
      ? values.pictures.map((p) => ({
          id: p.id,
          media_path: p.mediaPath,
          type: p.type as "image" | "thumbnail"
        }))
      : undefined,
    product_package:
      values.packages?.length && packagesDirty
        ? values.packages.map((p) => ({
            name: toUndef(p.name),
            is_free_trial:
              typeof p.isFreeTrial === "boolean" ? p.isFreeTrial : undefined,
            specifications: p.specifications,
            variants: p.variants?.map((v) => ({
              quantity: v.quantity,
              price: v.price,
              unit: toUndef(v.unit),
              provisioning_id: toUndef(v.provisioningId)
            }))
          }))
        : undefined,
    ...(categoryId === 1 && { product_api_package: values.apiPackage }),
    is_show_docs:
      typeof values.showOnBuyer === "boolean" ? values.showOnBuyer : undefined
  };
};
